import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import UserNav from '../../components/navbar/user-nav';
import AdminPanelSidebar from '../../components/admin-panel-sidebar';
import PageTitle from '../../components/page-title';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import { usePermissions } from '../../hooks/usePermissions';
import { RoleGate } from '../../components/RoleGate';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin } = usePermissions();
  const [sidebarShow, setSidebarShow] = useState(false);
  const [estadisticas, setEstadisticas] = useState({
    afiliados: 0,
    asociados: 0,
    propiedades: 0,
    agencias: 0,
    desarrollos: 0,
    talleres: 0,
    propiedadesSinAgencia: 0,
    usuariosSinFirebase: 0
  });
  const [cargandoStats, setCargandoStats] = useState(true);

  useEffect(() => {
    const cargarEstadisticas = async () => {
      if (!isAdmin()) {
        setCargandoStats(false);
        return;
      }

      try {
        // Cargar afiliados
        const afiliadosSnap = await getDocs(query(collection(db, 'asesor'), where('activo', '==', true)));

        // Cargar asociados
        const asociadosSnap = await getDocs(query(collection(db, 'asociado'), where('activo', '==', true)));

        // Cargar propiedades
        const propiedadesSnap = await getDocs(collection(db, 'propiedades'));
        const propiedades = propiedadesSnap.docs.map(doc => doc.data());

        // Contar propiedades sin agencia
        const sinAgencia = propiedades.filter(p =>
          !p.inmobiliaria_id && !p.inmobiliaria && !p.agencia_info?.inmobiliaria_id
        ).length;

        // Cargar agencias
        const agenciasSnap = await getDocs(collection(db, 'inmobiliaria'));

        // Cargar desarrollos
        const desarrollosSnap = await getDocs(collection(db, 'proyectos'));

        // Cargar talleres
        const talleresSnap = await getDocs(collection(db, 'taller'));

        // Contar usuarios sin Firebase Auth
        let sinFirebase = 0;
        afiliadosSnap.forEach(doc => {
          const data = doc.data();
          if (!data.firebase_uid) sinFirebase++;
        });
        asociadosSnap.forEach(doc => {
          const data = doc.data();
          if (!data.firebase_uid) sinFirebase++;
        });

        setEstadisticas({
          afiliados: afiliadosSnap.size,
          asociados: asociadosSnap.size,
          propiedades: propiedadesSnap.size,
          agencias: agenciasSnap.size,
          desarrollos: desarrollosSnap.size,
          talleres: talleresSnap.size,
          propiedadesSinAgencia: sinAgencia,
          usuariosSinFirebase: sinFirebase
        });
      } catch (error) {
        console.error('Error cargando estadísticas:', error);
      } finally {
        setCargandoStats(false);
      }
    };

    cargarEstadisticas();
  }, [isAdmin]);

  // Botones con configuración de permisos
  const botones = [
    {
      label: 'Todas las Agencias',
      path: '/admin-inm',
      roles: ['ADMIN', 'ASOCIADO'] as ('ADMIN' | 'ASOCIADO')[]
    },
    {
      label: 'Todos los Desarrollos',
      path: '/admin-project',
      roles: ['ADMIN', 'ASOCIADO'] as ('ADMIN' | 'ASOCIADO')[]
    },
    {
      label: 'Todas las Propiedades',
      path: '/admin-property',
      roles: ['ADMIN', 'ASOCIADO'] as ('ADMIN' | 'ASOCIADO')[]
    },
    {
      label: 'Todos los Afiliados',
      path: '/admin-afiliado',
      roles: ['ADMIN', 'ASOCIADO'] as ('ADMIN' | 'ASOCIADO')[]
    },
    {
      label: 'Todos los Asociados',
      path: '/admin-asociado',
      roles: ['ADMIN'] as ('ADMIN')[]
    },
    {
      label: 'Aprobar Reseñas',
      path: '/aprobar-resena',
      roles: ['ADMIN'] as ('ADMIN')[]
    },
    {
      label: 'Aprobar Propiedades',
      path: '/aprobar-propiedades',
      roles: ['ADMIN'] as ('ADMIN')[]
    },
    {
      label: 'Todos los Cursos',
      path: '/admin-talleres',
      roles: ['ADMIN'] as ('ADMIN')[]
    },
    {
      label: 'Escanear Asistencias',
      path: '/escanear-asistencia',
      roles: ['ADMIN'] as ('ADMIN')[]
    },
  ];

  // Herramientas administrativas especializadas - Solo las esenciales para operación diaria
  const herramientasAdmin = {
    'Talleres y Capacitación': [
      {
        label: 'Crear Taller',
        path: '/admin-create-taller',
        icon: 'fas fa-plus-circle',
        description: 'Crear nuevos talleres de capacitación',
        roles: ['ADMIN'] as ('ADMIN')[]
      },
    ],
    'Gestión de Relaciones': [
      {
        label: 'Asignaciones Bidireccionales',
        path: '/admin-asignaciones',
        icon: 'fas fa-exchange-alt',
        description: 'Asignar miembros a inmobiliarias, propiedades a miembros, y gestionar relaciones entre entidades',
        roles: ['ADMIN'] as ('ADMIN')[]
      },
    ]
  };

  // Mostrar mensaje de bienvenida personalizado
  const getWelcomeMessage = () => {
    if (!user) return "Cargando...";

    if (isAdmin()) {
      return `Bienvenido Administrador: ${user.nombre}`;
    }
    return `Bienvenido ${user.rol}: ${user.nombre}`;
  };

  return (
    <>
      <UserNav nombreUsuario={user?.nombre} nombreInmobiliaria={user?.inmobiliaria_nombre} />

      <PageTitle
        title="Panel de Administración"
        subtitle="Gestiona todos los recursos del sistema"
      />

      <section className="py-5">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="filter_search_opt">
                <Link to="#" className="btn btn-dark full-width mb-4" onClick={() => setSidebarShow(!sidebarShow)}>
                  Dashboard Navigation<i className="fa-solid fa-bars ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-md-12">
              <AdminPanelSidebar show={sidebarShow} setShow={setSidebarShow} />
            </div>

            <div className="col-lg-9 col-md-12">
              {/* Mensaje de bienvenida */}
              <div className="alert alert-info mb-4">
                <h4><i className="fa fa-dashboard me-2"></i>{getWelcomeMessage()}</h4>
                {user?.inmobiliaria_nombre && (
                  <p className="mb-0">
                    <i className="fa fa-building me-2"></i>
                    Inmobiliaria: <strong>{user.inmobiliaria_nombre}</strong>
                  </p>
                )}
              </div>

              {/* Estadísticas Rápidas */}
              {isAdmin() && (
                <div className="row mb-4">
                  <div className="col-12">
                    <h4 className="mb-3"><i className="fas fa-chart-bar me-2"></i>Estadísticas del Sistema</h4>
                    {cargandoStats ? (
                      <div className="text-center py-3">
                        <div className="spinner-border text-primary" role="status" />
                        <p className="mt-2">Cargando estadísticas...</p>
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card bg-primary text-white">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h5 className="mb-0">{estadisticas.afiliados}</h5>
                                  <small>Afiliados</small>
                                </div>
                                <i className="fas fa-users fa-2x opacity-50"></i>
                              </div>
                              <button
                                className="btn btn-sm btn-light mt-2"
                                onClick={() => navigate('/admin-afiliado')}
                              >
                                Ver todos
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card bg-success text-white">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h5 className="mb-0">{estadisticas.asociados}</h5>
                                  <small>Asociados</small>
                                </div>
                                <i className="fas fa-user-tie fa-2x opacity-50"></i>
                              </div>
                              <button
                                className="btn btn-sm btn-light mt-2"
                                onClick={() => navigate('/admin-asociado')}
                              >
                                Ver todos
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card bg-info text-white">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h5 className="mb-0">{estadisticas.propiedades}</h5>
                                  <small>Propiedades</small>
                                </div>
                                <i className="fas fa-home fa-2x opacity-50"></i>
                              </div>
                              <button
                                className="btn btn-sm btn-light mt-2"
                                onClick={() => navigate('/admin-property')}
                              >
                                Ver todas
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                          <div className="card bg-warning text-white">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h5 className="mb-0">{estadisticas.agencias}</h5>
                                  <small>Agencias</small>
                                </div>
                                <i className="fas fa-building fa-2x opacity-50"></i>
                              </div>
                              <button
                                className="btn btn-sm btn-light mt-2"
                                onClick={() => navigate('/admin-inm')}
                              >
                                Ver todas
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <h2 className="text-center mb-4">Panel de Control</h2>
              <div className="row">
                {botones.map((btn, index) => (
                  <div className="col-md-6 col-lg-4 mb-4" key={index}>
                    <RoleGate allowedRoles={btn.roles}>
                      <div className="card shadow-sm h-100">
                        <div className="card-body d-flex flex-column justify-content-between">
                          <h5 className="card-title">{btn.label}</h5>
                          <p className="text-muted small">
                            {btn.roles.includes('ADMIN' as any) && btn.roles.includes('ASOCIADO' as any)
                              ? 'Disponible para Administradores y Asociados'
                              : btn.roles.includes('ADMIN' as any)
                              ? 'Solo Administradores'
                              : 'Solo Asociados'
                            }
                          </p>
                          <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate(btn.path)}
                          >
                            <i className="fa fa-arrow-right me-2"></i>
                            Ir a {btn.label}
                          </button>
                        </div>
                      </div>
                    </RoleGate>
                  </div>
                ))}
              </div>

              {/* Sección de Herramientas Administrativas */}
              {isAdmin() && (
                <>
                  <hr className="my-5" />
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="text-primary mb-0">
                      <i className="fas fa-tools me-2"></i>
                      Herramientas Administrativas
                    </h3>
                    <span className="badge bg-danger">Solo Administradores</span>
                  </div>

                  {Object.entries(herramientasAdmin).map(([categoria, herramientas]) => (
                    <div key={categoria}>
                      <h5 className="mt-4 mb-3">
                        <i className="fas fa-folder me-2"></i>
                        {categoria}
                      </h5>
                      <div className="row">
                        {herramientas.map((herramienta, index) => (
                          <div className="col-md-6 col-lg-4 mb-4" key={index}>
                            <div className="card shadow-sm h-100 border-primary">
                              <div className="card-header bg-light">
                                <div className="d-flex align-items-center">
                                  <i className={`${herramienta.icon} me-2 text-primary`}></i>
                                  <h6 className="mb-0">{herramienta.label}</h6>
                                </div>
                              </div>
                              <div className="card-body d-flex flex-column">
                                <p className="text-muted small flex-grow-1">
                                  {herramienta.description}
                                </p>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => navigate(herramienta.path)}
                                >
                                  <i className={`${herramienta.icon} me-2`}></i>
                                  Abrir
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
